import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatRelativeDate } from '../utils';

const statusStyles = {
  open: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  closed: 'bg-slate-100 text-slate-600 border-slate-200',
  fulfilled: 'bg-sky-50 text-sky-700 border-sky-100',
  pending: 'bg-amber-50 text-amber-800 border-amber-100',
};

const MyPropertyRequests = ({ requests = [], isLoading }) => {
  const navigate = useNavigate();
  const list = Array.isArray(requests) ? requests : [];

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Notice Board requests</h2>
          <p className="text-xs text-slate-500 mt-0.5">Your posts — agents respond with matches</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/notice-board')}
          iconName="Plus"
          iconPosition="left"
        >
          New request
        </Button>
      </div>

      {isLoading ? (
        <div className="p-6 space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 rounded-lg bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-12 px-6">
          <div className="w-14 h-14 bg-violet-50 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icon name="ClipboardList" size={22} className="text-violet-500" />
          </div>
          <p className="font-medium text-slate-900 mb-1">No requests yet</p>
          <p className="text-sm text-slate-500 mb-4 max-w-sm mx-auto">
            Tell verified agents exactly what you need — location, budget, and must-haves.
          </p>
          <Button
            variant="default"
            onClick={() => navigate('/notice-board')}
            iconName="Plus"
            iconPosition="left"
          >
            Specific Property Request
          </Button>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {list.slice(0, 5).map((need) => {
            const status = String(need.status || 'open').toLowerCase();
            const style = statusStyles[status] || statusStyles.open;
            const responseCount =
              need.responses_count ??
              need.responses?.length ??
              need.response_count ??
              0;
            return (
              <li key={need.id}>
                <Link
                  to={`/notice-board/${need.id}`}
                  className="block px-5 py-4 hover:bg-slate-50/80 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span
                          className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${style}`}
                        >
                          {status}
                        </span>
                        {need.transaction_type && (
                          <span className="text-[10px] uppercase tracking-wide text-slate-500">
                            {need.transaction_type}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {need.title}
                      </p>
                      <p className="text-xs text-slate-500 mt-1 truncate">
                        {[need.city, need.area, need.location].filter(Boolean).join(' · ') ||
                          'Location open'}
                        {need.budget_max
                          ? ` · up to UGX ${Number(need.budget_max).toLocaleString()}`
                          : ''}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-semibold text-slate-700">
                        {responseCount} response{responseCount === 1 ? '' : 's'}
                      </p>
                      <p className="text-[11px] text-slate-400 mt-1">
                        {formatRelativeDate(need.created_at || need.updated_at)}
                      </p>
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default MyPropertyRequests;
